/**
 * Google Analytics 4 (GA4) gtag helper utilities
 * Updated: 2025-11-07 - Replaced Google Ads tag with GA4 tag (G-BGMRKPV1HK)
 * Updated: 2025-11-07 - Converted to GA4 custom events (seller_registration, valuation_complete, listing_created)
 * Updated: 2025-11-07 - Enhanced events with detailed tracking parameters for richer analytics
 */

declare global {
  interface Window {
    dataLayer: any[];
    gtag: (...args: any[]) => void;
  }
}

export const pageview = (url: string) => {
  if (typeof window !== 'undefined' && typeof window.gtag !== 'undefined') {
    window.gtag('config', 'G-BGMRKPV1HK', {
      page_path: url,
    });
  }
};

export const event = (action: string, params?: Record<string, any>) => {
  if (typeof window !== 'undefined' && typeof window.gtag !== 'undefined') {
    window.gtag('event', action, params);
  }
};

// Seller-specific GA4 custom events with enhanced parameters

export interface SellerRegistrationParams {
  method?: string; // e.g., 'email', 'google', 'phone'
  source?: string; // e.g., 'homepage', 'valuation_flow'
}

export interface ValuationCompleteParams {
  vehicle_make?: string;
  vehicle_model?: string;
  vehicle_year?: number;
  transmission?: string;
  mileage?: number;
  valuation_value?: number;
  price_tier?: string; // e.g., '0-15k', '15-20k', etc.
}

export interface ListingCreatedParams {
  vehicle_make?: string;
  vehicle_model?: string;
  vehicle_year?: number;
  transmission?: string;
  mileage?: number;
  listing_price?: number;
  vehicle_condition?: string;
}

export const trackSellerRegistration = (params?: SellerRegistrationParams) => {
  event('seller_registration', {
    event_category: 'Seller',
    event_label: 'Registration Complete',
    registration_method: params?.method,
    registration_source: params?.source,
  });
};

export const trackValuationComplete = (params?: ValuationCompleteParams) => {
  event('valuation_complete', {
    event_category: 'Valuation',
    event_label: 'VIN Valuation Complete',
    value: params?.valuation_value,
    currency: 'PLN',
    vehicle_make: params?.vehicle_make,
    vehicle_model: params?.vehicle_model,
    vehicle_year: params?.vehicle_year,
    transmission_type: params?.transmission,
    vehicle_mileage: params?.mileage,
    price_tier: params?.price_tier,
  });
};

export const trackListingCreated = (params?: ListingCreatedParams) => {
  event('listing_created', {
    event_category: 'Listing',
    event_label: 'Car Listing Created',
    value: params?.listing_price,
    currency: 'PLN',
    vehicle_make: params?.vehicle_make,
    vehicle_model: params?.vehicle_model,
    vehicle_year: params?.vehicle_year,
    transmission_type: params?.transmission,
    vehicle_mileage: params?.mileage,
    vehicle_condition: params?.vehicle_condition,
  });
};

// Form Engagement Tracking (for ad campaign analytics)

export interface FormEngagementParams {
  form_type: 'manual_valuation' | 'car_listing';
  source?: string; // e.g., 'direct', 'valuation_flow', 'dashboard'
  user_id?: string;
}

export const trackFormView = (params: FormEngagementParams) => {
  event('form_view', {
    event_category: 'Form Engagement',
    event_label: `${params.form_type} - Form Viewed`,
    form_type: params.form_type,
    source: params.source || 'direct',
    user_id: params.user_id,
  });
};

export const trackFormStart = (params: FormEngagementParams) => {
  event('form_start', {
    event_category: 'Form Engagement',
    event_label: `${params.form_type} - Form Started`,
    form_type: params.form_type,
    source: params.source || 'direct',
    user_id: params.user_id,
  });
};

export const trackFormSubmitAttempt = (params: FormEngagementParams) => {
  event('form_submit_attempt', {
    event_category: 'Form Engagement',
    event_label: `${params.form_type} - Submit Attempted`,
    form_type: params.form_type,
    source: params.source || 'direct',
    user_id: params.user_id,
  });
};

// Google Ads Conversion Tracking

export interface AdsConversionParams {
  value?: number;
  currency?: string;
  transaction_id?: string;
}

/**
 * Track Google Ads conversion for "Submit lead form"
 * Fires when a seller completes a key action (registration or listing creation)
 */
export const trackAdsConversion = (params?: AdsConversionParams) => {
  if (typeof window !== 'undefined' && typeof window.gtag !== 'undefined') {
    window.gtag('event', 'conversion', {
      send_to: 'AW-17711571220/k1mvCOO32LsbEJTCxP1B',
      value: params?.value || 1.0,
      currency: params?.currency || 'PLN',
      transaction_id: params?.transaction_id
    });
  }
};
